import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '@/lib/auth';
import { getInitData } from '@/lib/telegram';
import type { ApiResponse, MiniAppAuthResponse, TokenPair } from '@/types/api';

const baseURL = import.meta.env.VITE_API_BASE_URL || '';

export const client = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let waiters: {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}[] = [];

function drain(error: unknown, token: string | null) {
  waiters.forEach((w) => (token ? w.resolve(token) : w.reject(error)));
  waiters = [];
}

async function refreshWithRefreshToken(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  try {
    const { data } = await axios.post<ApiResponse<TokenPair>>(
      `${baseURL}/api/v1/auth/refresh`,
      { refresh_token: rt },
    );
    setTokens(data.data.access_token, data.data.refresh_token);
    return data.data.access_token;
  } catch {
    return null;
  }
}

async function reAuthFromInitData(): Promise<string | null> {
  const raw = getInitData();
  if (!raw) return null;
  try {
    const { data } = await axios.post<ApiResponse<MiniAppAuthResponse>>(
      `${baseURL}/api/v1/miniapp/auth`,
      null,
      { headers: { Authorization: `tma ${raw}` } },
    );
    setTokens(data.data.access_token, data.data.refresh_token);
    return data.data.access_token;
  } catch {
    return null;
  }
}

client.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    if (original.url?.includes('/miniapp/auth') || original.url?.includes('/auth/refresh')) {
      clearTokens();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        waiters.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        original._retry = true;
        return client(original);
      });
    }

    original._retry = true;
    isRefreshing = true;
    try {
      let newToken = await refreshWithRefreshToken();
      if (!newToken) {
        newToken = await reAuthFromInitData();
      }
      if (!newToken) {
        drain(error, null);
        clearTokens();
        return Promise.reject(error);
      }
      drain(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return client(original);
    } catch (err) {
      drain(err, null);
      clearTokens();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

export async function exchangeInitData(): Promise<MiniAppAuthResponse> {
  const raw = getInitData();
  if (!raw) {
    throw new Error('No Telegram initData available');
  }
  const { data } = await axios.post<ApiResponse<MiniAppAuthResponse>>(
    `${baseURL}/api/v1/miniapp/auth`,
    null,
    { headers: { Authorization: `tma ${raw}` } },
  );
  setTokens(data.data.access_token, data.data.refresh_token);
  return data.data;
}
